class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html, new URL(url, window.location.origin).searchParams.get('sort_by'));
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html, new URL(window.location.href).searchParams.get('sort_by'));
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html, sortBy) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    FacetFiltersForm.sortVisibleProductsByDisplayedPrice(sortBy);

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    document.dispatchEvent(new CustomEvent('xb-wishlist:update-state'));
    document.dispatchEvent(new CustomEvent('wishlist:updated'));
  }

  static sortVisibleProductsByDisplayedPrice(sortBy) {
    if (sortBy !== 'price-ascending' && sortBy !== 'price-descending') return;

    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    const productItems = Array.from(productGrid.children).filter((item) =>
      item.querySelector('.kaj-product-card[data-product-price]')
    );

    productItems
      .map((item, index) => {
        const price = Number(item.querySelector('.kaj-product-card').dataset.productPrice);
        return { item, index, price: Number.isFinite(price) ? price : Number.POSITIVE_INFINITY };
      })
      .sort((a, b) => {
        const difference = sortBy === 'price-ascending' ? a.price - b.price : b.price - a.price;
        return difference || a.index - b.index;
      })
      .forEach(({ item }) => productGrid.append(item));
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    // If reset or filter removal triggered, sync price-range markup from response
    const isReset = event && event.target && (event.target.closest('.facets__reset') || event.target.closest('facet-remove') || event.target.closest('.js-facet-remove'));
    if (isReset) {
      const newPriceRanges = parsedHTML.querySelectorAll('price-range');
      const curPriceRanges = document.querySelectorAll('price-range');
      curPriceRanges.forEach((cur, i) => {
        if (newPriceRanges[i]) {
          cur.innerHTML = newPriceRanges[i].innerHTML;
        }
      });
    }

    document.querySelectorAll('price-range').forEach((element) => {
      if (typeof element.initSlider === 'function') {
        element.initSlider();
      }
    });

    // Update KAJ mobile filter sheet body and facet counts
    if (typeof window.updateKajMobileSheetBody === 'function') {
      window.updateKajMobileSheetBody(html);
    } else {
      const newMobileSheetBody = parsedHTML.querySelector('.kaj-filter-sheet-body');
      const currentMobileSheetBody = document.querySelector('.kaj-filter-sheet-body');
      if (newMobileSheetBody && currentMobileSheetBody) {
        const collapsedLabels = new Set();
        currentMobileSheetBody.querySelectorAll('.kaj-filter-accordion:not(.is-open)').forEach((acc) => {
          if (acc.dataset.filterLabel) collapsedLabels.add(acc.dataset.filterLabel);
        });

        currentMobileSheetBody.innerHTML = newMobileSheetBody.innerHTML;

        if (collapsedLabels.size > 0) {
          currentMobileSheetBody.querySelectorAll('.kaj-filter-accordion').forEach((acc) => {
            if (collapsedLabels.has(acc.dataset.filterLabel)) {
              acc.classList.remove('is-open');
              const btn = acc.querySelector('.kaj-filter-accordion__header');
              if (btn) btn.setAttribute('aria-expanded', 'false');
            }
          });
        }

        if (typeof window.setupKajMobileFilterSheet === 'function') {
          window.setupKajMobileFilterSheet();
        }
      }
    }

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      if (value !== '') {
        searchParams.append(key, value);
      }
    }
    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const sourceForm = event.target.closest('form');

    // The mobile sort control is separate from the mobile filter form. Retain
    // active filters from the URL instead of letting the hidden desktop sort
    // control overwrite the selected mobile value.
    if (sourceForm?.id === 'FacetSortMobileForm') {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('sort_by', event.target.value);
      this.onSubmitForm(searchParams.toString(), event);
      return;
    }

    const targetElement = event.target || (event.srcElement ? event.srcElement : null);
    if (targetElement && targetElement.classList && targetElement.classList.contains('mobile-facets__checkbox')) {
      const searchParams = this.createSearchParams(targetElement.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = sourceForm.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

const sortInitialProductGrid = () => {
  FacetFiltersForm.sortVisibleProductsByDisplayedPrice(new URL(window.location.href).searchParams.get('sort_by'));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', sortInitialProductGrid, { once: true });
} else {
  sortInitialProductGrid();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.boundOnPointerMove = this.onPointerMove.bind(this);
    this.boundStopDragging = this.stopDragging.bind(this);
  }

  connectedCallback() {
    this.initSlider();
  }

  disconnectedCallback() {
    this.cleanupListeners();
  }

  cleanupListeners() {
    document.removeEventListener('pointermove', this.boundOnPointerMove);
    document.removeEventListener('pointerup', this.boundStopDragging);
    document.removeEventListener('pointercancel', this.boundStopDragging);
  }

  initSlider() {
    this.track = this.querySelector('[data-price-track]');
    this.fill = this.querySelector('[data-price-fill]');
    this.minHandle = this.querySelector('[data-price-handle="min"]');
    this.maxHandle = this.querySelector('[data-price-handle="max"]');
    this.minLabel = this.querySelector('[data-price-min-label]');
    this.maxLabel = this.querySelector('[data-price-max-label]');
    this.minInput = this.querySelector('[data-price-min-input]') || this.querySelectorAll('input')[0];
    this.maxInput = this.querySelector('[data-price-max-input]') || this.querySelectorAll('input')[1];

    if (!this.minInput || !this.maxInput) return;

    // Determine max price limit
    const container = this.querySelector('[data-custom-price-filter]');
    let maxPrice = container ? parseFloat(container.dataset.maxPrice) : NaN;
    if (!Number.isFinite(maxPrice) || maxPrice <= 0) {
      maxPrice = parseFloat(this.maxInput.getAttribute('max')) || 10000;
    }
    this.maxPrice = maxPrice;

    // If slider elements are present, set up dual-range slider
    if (this.track && this.minHandle && this.maxHandle) {
      this.setupSlider();
    } else {
      this.setupInputsOnly();
    }
  }

  setupSlider() {
    this.dragging = false;
    this.activeHandle = null;
    this.pointerId = null;

    // Read current values from URL or inputs
    const urlParams = new URLSearchParams(window.location.search);
    const container = this.querySelector('[data-custom-price-filter]');
    const minParam = container?.dataset.minParam || 'filter.v.price.gte';
    const maxParam = container?.dataset.maxParam || 'filter.v.price.lte';

    let initialMin = 0;
    let initialMax = this.maxPrice;

    if (urlParams.has(minParam) && urlParams.get(minParam) !== '') {
      initialMin = parseFloat(urlParams.get(minParam));
    } else if (this.minInput.value !== '') {
      initialMin = parseFloat(this.minInput.value);
    }

    if (urlParams.has(maxParam) && urlParams.get(maxParam) !== '') {
      initialMax = parseFloat(urlParams.get(maxParam));
    } else if (this.maxInput.value !== '') {
      initialMax = parseFloat(this.maxInput.value);
    }

    if (!urlParams.has(minParam)) {
      initialMin = 0;
      this.minInput.value = '';
    }
    if (!urlParams.has(maxParam)) {
      initialMax = this.maxPrice;
      this.maxInput.value = '';
    }

    this.minVal = Math.max(0, Math.min(this.maxPrice, Number.isFinite(initialMin) ? initialMin : 0));
    this.maxVal = Math.max(0, Math.min(this.maxPrice, Number.isFinite(initialMax) ? initialMax : this.maxPrice));
    if (this.minVal > this.maxVal) this.minVal = this.maxVal;

    this.updateUI();

    // Bind handle pointer events
    this.minHandle.onpointerdown = (e) => this.startDragging('min', e);
    this.maxHandle.onpointerdown = (e) => this.startDragging('max', e);

    // Track click
    this.track.onclick = (e) => this.onTrackClick(e);
    const sliderWrap = this.querySelector('.custom-price-slider');
    if (sliderWrap) {
      sliderWrap.onclick = (e) => {
        if (e.target === this.minHandle || e.target === this.maxHandle || e.target.closest('button')) return;
        this.onTrackClick(e);
      };
    }

    // Input event listeners
    this.minInput.oninput = () => {
      let val = this.minInput.value === '' ? 0 : parseFloat(this.minInput.value);
      if (!Number.isFinite(val)) val = 0;
      this.minVal = Math.max(0, Math.min(this.maxVal, val));
      this.updateUI('minInput');
    };

    this.minInput.onchange = () => {
      let val = this.minInput.value === '' ? 0 : parseFloat(this.minInput.value);
      if (!Number.isFinite(val)) val = 0;
      this.minVal = Math.max(0, Math.min(this.maxVal, val));
      this.updateUI();
      this.notifyFormChange(true);
    };

    this.maxInput.oninput = () => {
      let val = this.maxInput.value === '' ? this.maxPrice : parseFloat(this.maxInput.value);
      if (!Number.isFinite(val)) val = this.maxPrice;
      this.maxVal = Math.max(this.minVal, Math.min(this.maxPrice, val));
      this.updateUI('maxInput');
    };

    this.maxInput.onchange = () => {
      let val = this.maxInput.value === '' ? this.maxPrice : parseFloat(this.maxInput.value);
      if (!Number.isFinite(val)) val = this.maxPrice;
      this.maxVal = Math.max(this.minVal, Math.min(this.maxPrice, val));
      this.updateUI();
      this.notifyFormChange(true);
    };

    // Keyboard support
    [this.minInput, this.maxInput].forEach((input) => {
      input.onkeydown = (e) => {
        if (e.key === 'Enter') {
          this.notifyFormChange(true);
          return;
        }
        this.onKeyDown(e);
      };
    });
  }

  setupInputsOnly() {
    this.minInput.onchange = () => this.notifyFormChange(true);
    this.maxInput.onchange = () => this.notifyFormChange(true);
  }

  onKeyDown(event) {
    if (event.metaKey || event.ctrlKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  valToPercent(val) {
    if (this.maxPrice <= 0) return 0;
    return (val / this.maxPrice) * 100;
  }

  formatMoney(val) {
    return Math.round(val).toLocaleString('en-IN');
  }

  updateUI(source = null) {
    const minPct = Math.max(0, Math.min(100, this.valToPercent(this.minVal)));
    const maxPct = Math.max(0, Math.min(100, this.valToPercent(this.maxVal)));

    if (this.minHandle) this.minHandle.style.left = `${minPct}%`;
    if (this.maxHandle) this.maxHandle.style.left = `${maxPct}%`;

    if (this.fill) {
      this.fill.style.left = `${minPct}%`;
      this.fill.style.width = `${Math.max(0, maxPct - minPct)}%`;
    }

    if (this.minLabel) this.minLabel.textContent = `₹${this.formatMoney(this.minVal)}`;
    if (this.maxLabel) this.maxLabel.textContent = `₹${this.formatMoney(this.maxVal)}`;

    if (source !== 'minInput' && this.minInput) {
      this.minInput.value = this.minVal > 0 ? Math.round(this.minVal) : '';
    }

    if (source !== 'maxInput' && this.maxInput) {
      this.maxInput.value = this.maxVal < this.maxPrice ? Math.round(this.maxVal) : '';
    }

    // Dynamic z-index so overlapping handles can both be grabbed
    if (this.minHandle && this.maxHandle) {
      if (this.activeHandle === 'min') {
        this.minHandle.style.zIndex = '8';
        this.maxHandle.style.zIndex = '6';
      } else if (this.activeHandle === 'max') {
        this.minHandle.style.zIndex = '6';
        this.maxHandle.style.zIndex = '8';
      } else if (this.minVal > this.maxPrice / 2) {
        this.minHandle.style.zIndex = '7';
        this.maxHandle.style.zIndex = '6';
      } else {
        this.minHandle.style.zIndex = '6';
        this.maxHandle.style.zIndex = '7';
      }
    }
  }

  startDragging(handle, event) {
    event.preventDefault();
    event.stopPropagation();

    this.dragging = true;
    this.activeHandle = handle;
    this.pointerId = event.pointerId;

    const target = event.currentTarget;
    if (event.pointerId !== undefined && target.setPointerCapture) {
      try {
        target.setPointerCapture(event.pointerId);
      } catch (_) {}
    }

    target.classList.add('is-dragging');
    this.updateUI();

    document.addEventListener('pointermove', this.boundOnPointerMove, { passive: false });
    document.addEventListener('pointerup', this.boundStopDragging, { passive: false });
    document.addEventListener('pointercancel', this.boundStopDragging, { passive: false });
  }

  onPointerMove(event) {
    if (!this.dragging || !this.track) return;
    if (this.pointerId !== null && event.pointerId !== undefined && event.pointerId !== this.pointerId) return;

    event.preventDefault();
    event.stopPropagation();

    const rect = this.track.getBoundingClientRect();
    if (!rect.width) return;

    let pct = (event.clientX - rect.left) / rect.width;
    pct = Math.max(0, Math.min(1, pct));
    const newVal = Math.round(pct * this.maxPrice);

    // Smart overlap handling: if both handles are at the same value,
    // dragging left automatically moves the min handle down,
    // and dragging right moves the max handle up.
    if (this.minVal === this.maxVal) {
      if (newVal < this.minVal) {
        this.activeHandle = 'min';
      } else if (newVal > this.maxVal) {
        this.activeHandle = 'max';
      }
    }

    if (this.activeHandle === 'min') {
      this.minVal = Math.min(newVal, this.maxVal);
    } else {
      this.maxVal = Math.max(newVal, this.minVal);
    }

    this.updateUI();
  }

  stopDragging(event) {
    if (!this.dragging) return;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.dragging = false;
    this.pointerId = null;

    if (this.minHandle) this.minHandle.classList.remove('is-dragging');
    if (this.maxHandle) this.maxHandle.classList.remove('is-dragging');

    this.cleanupListeners();
    this.activeHandle = null;
    this.updateUI();
    this.notifyFormChange(true);
  }

  onTrackClick(event) {
    if (this.dragging) return;
    if (event.target === this.minHandle || event.target === this.maxHandle) return;

    const rect = this.track.getBoundingClientRect();
    if (!rect.width) return;

    let pct = (event.clientX - rect.left) / rect.width;
    pct = Math.max(0, Math.min(1, pct));
    const clickedVal = Math.round(pct * this.maxPrice);

    if (clickedVal > this.maxVal) {
      this.maxVal = clickedVal;
      this.activeHandle = 'max';
    } else if (clickedVal < this.minVal) {
      this.minVal = clickedVal;
      this.activeHandle = 'min';
    } else {
      const distToMin = Math.abs(clickedVal - this.minVal);
      const distToMax = Math.abs(clickedVal - this.maxVal);

      if (distToMin <= distToMax) {
        this.minVal = clickedVal;
        this.activeHandle = 'min';
      } else {
        this.maxVal = clickedVal;
        this.activeHandle = 'max';
      }
    }

    this.updateUI();
    this.activeHandle = null;
    this.notifyFormChange(true);
  }

  notifyFormChange(immediate = false) {
    const form = this.closest('form');
    if (!form) return;

    if (immediate) {
      const facetForm = form.closest('facet-filters-form');
      if (facetForm && typeof facetForm.onSubmitHandler === 'function') {
        const fakeTarget = this.minInput || form;
        facetForm.onSubmitHandler({ preventDefault: () => {}, target: fakeTarget, srcElement: fakeTarget });
        return;
      }
    }
    form.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
